import { BlueprintEdge } from '../edge.js';
import * as constants from '../constants.js';

export class BlueprintLine extends BlueprintEdge {
  /**
   * The terminology is from vector math
   *   ·---------->
   * Tail         Head
   */

  /** @type {fabric.Shape|null} - The start point custom control object */
  #tailCircle = null;

  /** @type {fabric.Shape|null} - The end point custom control object */
  #headCircle = null;

  /** @type {fabric.Shape|null} - The vertex the start point is snapped to */
  #tailVertex = null;

  /** @type {number} - The index of the snapPoints array from tailVertex shape */
  #tailVertexIdx = null;

  /** @type {fabric.Shape|null} - The vertex the end point is snapped to */
  #headVertex = null;

  /** @type {number} - The index of the snapPoints array from headVertex shape */
  #headVertexIdx = null;

  /** @type {fabric.Triangle|null} - The arrow-header at the end point */
  #arrowHead = null;

  /** @type {{x: number, y:number} | null} - Cache for line position */
  #lastClickPos = null;

  /**
   * @param {fabric.Canvas} canvas - The fabric canvas
   * @param {Object} config - Configuration for the line
   * @param {Object.<string, { start: {x: number, y:number}, end: {x: number, y:number}}>} config.coords - The start and end coordinates in a flat array. e.g. [x1, y1, x2, y2]
   * @param {boolean=} config.arrow - Whether to add an arrow-head to the end of the line
   * @param {'solid'|'dashed'|'thick'} config.style - Whether to add an arrow-head to the end of the line
   * @param {Object=} config.controls - Controls configuration
   * @param {string=} config.controls.fill - The fill value for the endpoint circle
   * @param {string=} config.controls.stroke - The stroke value for the endpoint circle
   * @param {Object=} config.fabricOptions - Line options to be passed into fabric
   * @param {fabric.Object} [config.fabricObject] - an existing fabric object.
   * @param {string} config.readonly - Flag for readonly user - used to disable the showing and interaction of the head & tail circles
   * @returns {BlueprintLine}
   */
  constructor(
    canvas,
    {
      coords = {
        tail: { x: 0, y: 0 },
        head: { x: 50, y: 0 },
      },
      arrow = false,
      style = 'solid',
      controls: { fill = 'white', stroke = 'grey' } = {},
      fabricOptions = {},
      fabricObject,
      readonly,
    },
  ) {
    super(canvas, { edgeType: constants.CELL_TYPES.line });

    this.readonly = readonly;

    const { tail, head } = coords;

    /** @todo Fix this for loading saved lines */
    if (fabricObject) {
      const tempPosition = { x: fabricObject.left, y: fabricObject.top };

      this.ref = fabricObject;

      this.#createControls(tempPosition, tempPosition, { fill, stroke });

      if (fabricObject.bpLineArrow)
        this.#createArrow(tempPosition, fabricObject.angle);

      // reset control and arrowhead positions based on line position
      this.#handleLineMovement();

      /** @todo Maybe rethink the method structure here? */
      this.#addComponents();
      this.#assignMovementHandlers();
      this.#assignObjectRemovedHandler();
      this.#assignRotationScaleHandler();

      this.#snapToPoint(this.#tailCircle);
      this.#snapToPoint(this.#headCircle);
      return;
    }

    const lineProps = {
      strokeWidth: 1,
      stroke: 'grey',
      hasControls: false,
      hasBorders: false,
      // these must be set to this value for the angle/distance calculations to work
      originX: 'left',
      originY: 'center',
      padding: 15,
      bpLineArrow: arrow,
      ...fabricOptions,
    };

    switch (style) {
      case 'dashed':
        lineProps.strokeDashArray = [10];
        break;
      case 'thick':
        lineProps.strokeWidth = 3;
        break;
      default:
        break;
    }

    /** @type fabric.Line */
    this.ref = new fabric.Line([tail.x, tail.y, head.x, head.y], lineProps);

    if (arrow) this.#createArrow(head, 90);

    this.#createControls(tail, head, { fill, stroke });

    // reset control and arrowhead positions based on line position
    this.#handleLineMovement(this.ref);

    this.#assignComponentsAddHandler();
    this.#assignObjectRemovedHandler();
    this.#assignMovementHandlers();
    this.#assignRotationScaleHandler();
  }

  get selected() {
    return this.canvas?.getActiveObjects()?.includes(this.ref);
  }

  #assignMovementHandlers = () => {
    const { canvas } = this;
    canvas.on('object:moving', ({ target }) => {
      this.#handleControlMovement(target);
      this.#handleLineMovement(target);
      this.#handleAttachmentMoving(target);

      this.render();
    });
    canvas.on('object:rotating', ({ target }) => {
      this.#groupSelectionCalc(target);
    });
  };

  #assignRotationScaleHandler = () => {
    this.canvas.on('object:rotating', ({ target }) => {
      if (
        (!this.#headVertex && !this.#tailVertex) ||
        ![this.#headVertex, this.#tailVertex].includes(target)
      )
        return;

      this.#rotateScaleShapeSnapped(target);
    });

    this.canvas.on('object:scaling', ({ target }) => {
      if (
        (!this.#headVertex && !this.#tailVertex) ||
        ![this.#headVertex, this.#tailVertex].includes(target)
      )
        return;

      this.#rotateScaleShapeSnapped(target);
    });
  };

  #snapToPoint = target => {
    this.canvas.forEachObject(vertex => {
      if (
        // only match with shape (vertices)
        Object.values(constants.SHAPE_TYPES).every(
          type => type.toLowerCase() !== vertex.type,
        ) ||
        // ignore the endpoint circles
        [this.#tailCircle, this.#headCircle, this.#arrowHead].includes(vertex)
      )
        return;

      const circlePosition = { x: target.left, y: target.top };
      const snapPoints = this.getVertexSnappingPoints(vertex);
      for (const point of snapPoints) {
        if (this.getPointDistance(circlePosition, point) < this.snapThreshold) {
          const { x: left, y: top } = point;
          target.set({ left, top }).setCoords();
          if (target === this.#headCircle) {
            this.#headVertex = vertex;
            this.#headVertexIdx = snapPoints.indexOf(point);
          } else {
            this.#tailVertex = vertex;
            this.#tailVertexIdx = snapPoints.indexOf(point);
          }
          break;
        }
      }
    });
  };

  /**
   * Sets headCircle or tailCircle to the new point on snapped shape after rotation or scalling.
   * @param {*} target - shape being rotated
   */
  #rotateScaleShapeSnapped = target => {
    const snapPoints = this.getVertexSnappingPoints(target);
    let currentSnap;
    let lineSide;

    if (target === this.#headVertex) {
      currentSnap = snapPoints[this.#headVertexIdx];
      lineSide = this.#headCircle;
    } else {
      currentSnap = snapPoints[this.#tailVertexIdx];
      lineSide = this.#tailCircle;
    }

    const { x: left, y: top } = currentSnap;
    lineSide.set({ left, top }).setCoords();
    this.#handleControlMovement(lineSide);
  };

  /**
   * Add components (arrow, controls, etc) to a line that is already on the canvas
   */
  #addComponents = () => {
    const { canvas } = this;

    // keep the arrow endcap below the movement circles
    if (this.#arrowHead) canvas.add(this.#arrowHead);

    canvas.add(this.#tailCircle, this.#headCircle);
    canvas.renderAll();
  };

  /**
   * For when a new line is created and isn't present on the canvas
   */
  #assignComponentsAddHandler = () => {
    const { canvas } = this;
    canvas.on('object:added', ({ target }) => {
      if (target === this.ref) this.#addComponents();
    });
  };

  #createControls = (tail, head, { fill, stroke }) => {
    const { canvas } = this;
    const circleProps = {
      fill,
      stroke,
      radius: 5,
      padding: 3,
      originX: 'center',
      originY: 'center',
      visible: false,
      hasControls: false,
      hasBorders: false,
      excludeFromExport: true, // don't include in JSON output
    };

    /** @todo do we get the default colors from above? */
    this.#tailCircle = new fabric.Circle({
      ...circleProps,
      top: tail.y,
      left: tail.x,
    }).setCoords();

    this.#headCircle = new fabric.Circle({
      ...circleProps,
      top: head.y,
      left: head.x,
    }).setCoords();

    /** @todo Verify all this hide/show logic */
    canvas.on('mouse:down', ({ target }) => {
      if (this.readonly) return;

      if (
        this.#isComponent(target) &&
        (!this.#headCircle.visible || !this.#tailCircle.visible)
      ) {
        this.#updateCircleVisibility(true);
      } else if (!this.selected) {
        this.#updateCircleVisibility(false);
      } else if (target === this.ref) {
        this.#lastClickPos = { x: target.left, y: target.top };
      } else {
        this.#lastClickPos = null;
      }
    });
    canvas.on('selection:created', () => {
      const activeObject = canvas.getActiveObject();
      const isGroupSelection = activeObject?.type === 'activeSelection';
      const groupHasHead = activeObject._objects?.includes(this.#headCircle);
      const groupHasTail = activeObject._objects?.includes(this.#tailCircle);

      if (isGroupSelection) {
        if (groupHasHead) activeObject.removeWithUpdate(this.#headCircle);
        if (groupHasTail) activeObject.removeWithUpdate(this.#tailCircle);
        this.render();
      } else if (this.selected) {
        this.#updateCircleVisibility(true);
      }
    });
    canvas.on('mouse:over', ({ target }) => {
      if (this.readonly) return;

      if (
        this.#isComponent(target) &&
        !this.selected &&
        (!this.#headCircle.visible || !this.#tailCircle.visible)
      ) {
        this.#updateCircleVisibility(true);
      }
    });
    canvas.on('selection:cleared', () => {
      this.#updateCircleVisibility(false);
    });
    canvas.on('mouse:out', ({ target }) => {
      if (this.#isComponent(target) && !this.selected) {
        this.#updateCircleVisibility(false);
      }
    });
  };

  /**
   * Calculates the transform matrix and sets the new positions for tailCircle & headCircle on group movement or group rotation
   * @param {target}  - either element or group selection being moved or rotated
   */
  #groupSelectionCalc = target => {
    if (
      target.type !== 'activeSelection' ||
      !target.getObjects?.().includes(this.ref)
    )
      return;

    // Combined transformation matrix for the line
    const mTotal = fabric.util.multiplyTransformMatrices(
      target.calcTransformMatrix(), // Group's transformation matrix
      this.ref.calcTransformMatrix(true), // Line's local transformation matrix
    );

    // Decompose the combined matrix to get the total angle and position
    const decomposedTotal = fabric.util.qrDecompose(mTotal);

    // Calculate the line's endpoints based on its width and decomposed angle
    const radians = this.getRadians(decomposedTotal.angle);
    const halfWidth = this.ref.width / 2;
    const offsetX = Math.cos(radians) * halfWidth;
    const offsetY = Math.sin(radians) * halfWidth;

    // Center position from decomposed matrix
    const centerX = decomposedTotal.translateX;
    const centerY = decomposedTotal.translateY;

    // Calculate transformed endpoints directly without additional transformation
    const transformedStartPoint = {
      x: centerX - offsetX,
      y: centerY - offsetY,
    };
    const transformedEndPoint = {
      x: centerX + offsetX,
      y: centerY + offsetY,
    };

    this.#tailCircle
      .set({
        left: transformedStartPoint.x,
        top: transformedStartPoint.y,
      })
      .setCoords()
      // Ensure that the circles stay above the line in the canvas rendering order
      .bringToFront();

    this.#headCircle
      .set({
        left: transformedEndPoint.x,
        top: transformedEndPoint.y,
      })
      .setCoords()
      // Ensure that the circles stay above the line in the canvas rendering order
      .bringToFront();

    // Manually set this instead of using the other method, maybe the method can be renamed
    if (!this.#arrowHead) return;
    this.#arrowHead
      .set({
        // maintain the triangle's perpendicularity
        angle: decomposedTotal.angle + 90,
        left: transformedEndPoint.x,
        top: transformedEndPoint.y,
      })
      .setCoords();
  };

  #isComponent = target =>
    [this.ref, this.#headCircle, this.#tailCircle].includes(target);

  #updateCircleVisibility = visible => {
    this.#tailCircle.set({ visible });
    this.#headCircle.set({ visible });
    this.render();
  };

  #assignObjectRemovedHandler = () => {
    const { canvas } = this;

    canvas.on('object:removed', ({ target }) => {
      if (target === this.ref) {
        canvas.remove(this.#tailCircle, this.#headCircle, this.#arrowHead);
      }
    });
  };

  #createArrow = ({ x: left, y: top }, angle) => {
    this.#arrowHead = new fabric.Triangle({
      left,
      top,
      angle,
      fill: this.ref.stroke,
      originX: 'center',
      originY: 'top',
      height: 12,
      width: 8,
      selectable: false,
      hasControls: false,
      hasBorders: false,
      evented: false,
      excludeFromExport: true,
    });
  };

  /**
   * @param {fabric.Object|{top: number, left: number}} movingControl - The new or moving point
   * @param {fabric.Object|{top: number, left: number}} anchorControl - The static oppositional point
   * @param {boolean} invert - Whether to invert the rotation angle, in the case of the movingControl being the tail
   */
  #transformLine = (
    { top: movingTop, left: movingLeft },
    { top: anchorTop, left: anchorLeft },
    invert = false,
  ) => {
    const movingCoords = { x: movingLeft, y: movingTop };
    const anchorCoords = { x: anchorLeft, y: anchorTop };

    const left = invert ? movingLeft : anchorLeft;
    const top = invert ? movingTop : anchorTop;

    // Calculate the angle between the two points
    const angle = invert
      ? this.getAngleFromPoints(anchorCoords, movingCoords)
      : this.getAngleFromPoints(movingCoords, anchorCoords);

    // Calculate the distance between circles
    const width = this.getPointDistance(movingCoords, anchorCoords);

    this.ref.set({ angle, width, left, top });

    // Make sure the line's coordinates and angle are up to date
    this.ref.setCoords();
  };

  #handleControlMovement = target => {
    if (![this.#tailCircle, this.#headCircle].includes(target)) return;

    // set the non-moving circle
    let staticCircle;

    if (target === this.#tailCircle) {
      staticCircle = this.#headCircle;
    } else {
      staticCircle = this.#tailCircle;
    }

    if (this.#headVertex && target === this.#headCircle) {
      this.#headVertex = null;
      this.#headVertexIdx = null;
    } else if (this.#tailVertex && target === this.#tailCircle) {
      this.#tailVertex = null;
      this.#tailVertexIdx = null;
    }

    this.#snapToPoint(target);

    this.#transformLine(target, staticCircle, target === this.#tailCircle);
    this.#adjustArrowHead(target);
  };

  #adjustArrowHead = (control = this.#headCircle) => {
    if (!this.#arrowHead) return;

    const lineAngle = this.ref.setCoords().get('angle');

    this.#arrowHead
      .set({
        // maintain the triangle's perpendicularity
        angle: control === this.#headCircle ? lineAngle + 90 : lineAngle - 270,
        left: this.#headCircle.left,
        top: this.#headCircle.top,
      })
      .setCoords();
  };

  #handleLineMovement = (target = this.ref) => {
    const isGroupSelection = target?.type === 'activeSelection';
    const groupIncludesTarget = target._objects?.includes(this.ref);

    if (
      (isGroupSelection && !groupIncludesTarget) ||
      (!isGroupSelection && target !== this.ref)
    )
      return;

    if (this.#lastClickPos) {
      // If the line is currently snapped and moved less than the snapThreshold then the line should stay snapped
      if (
        this.#headVertex &&
        this.#tailVertex &&
        this.getPointDistance(
          { x: target.left, y: target.top },
          this.#lastClickPos,
        ) < this.snapThreshold
      ) {
        target.set('left', this.#lastClickPos.x);
        target.set('top', this.#lastClickPos.y);
      } else {
        this.#headVertex = null;
        this.#headVertexIdx = null;
        this.#tailVertex = null;
        this.#tailVertexIdx = null;
      }
    }
    if (isGroupSelection) {
      this.#groupSelectionCalc(target);
    } else {
      this.#repositionControls();
      this.#adjustArrowHead();
    }

    // Update line coords in fabric
    this.ref.setCoords();
  };

  #repositionControls = () => {
    const { ref } = this;
    // Calculate the angle of rotation in radians
    const angleRad = this.getRadians(ref.angle);

    // Calculate the positions for the start circle
    this.#tailCircle
      .set({
        left: ref.left,
        top: ref.top,
      })
      .setCoords()
      // Ensure that the circles stay above the line in the canvas rendering order
      .bringToFront();

    // Calculate the positions for the end circle
    const endX = ref.left + Math.cos(angleRad) * ref.width;
    const endY = ref.top + Math.sin(angleRad) * ref.width;

    this.#headCircle
      .set({
        left: endX,
        top: endY,
      })
      .setCoords()
      // Ensure that the circles stay above the line in the canvas rendering order
      .bringToFront();
  };

  #getNewLineEndpointPosition = (snappingPoints, currentEndpointPosition) => {
    // Calculate the distances between the current endpoint position and all snapping points
    const distances = snappingPoints.map(point =>
      this.getPointDistance(point, currentEndpointPosition),
    );

    // Find the index of the snapping point closest to the current endpoint position
    const closestIndex = distances.indexOf(Math.min(...distances));

    // Return the position of the closest snapping point
    return {
      x: snappingPoints[closestIndex].x,
      y: snappingPoints[closestIndex].y,
    };
  };

  #handleAttachmentMoving = target => {
    /** @todo We also need to handle group scaling */
    if (![this.#tailVertex, this.#headVertex].includes(target)) return;

    const { canvas } = this;
    const isHead = target === this.#headVertex;
    const snappingPoints = this.getVertexSnappingPoints(target);
    const nextPosition = this.#getNewLineEndpointPosition(
      snappingPoints,
      // this needs to change
      {
        x: isHead ? this.#headCircle.left : this.#tailCircle.left,
        y: isHead ? this.#headCircle.top : this.#tailCircle.top,
      },
    );

    // If connected at the head
    if (isHead) {
      this.#transformLine(
        { top: nextPosition.y, left: nextPosition.x },
        { top: this.#tailCircle.top, left: this.#tailCircle.left },
      );

      this.#headCircle.set({ left: nextPosition.x, top: nextPosition.y });
      // If connected at the tail
    } else {
      this.#transformLine(
        { top: nextPosition.y, left: nextPosition.x },
        { top: this.#headCircle.top, left: this.#headCircle.left },
        true,
      );

      this.#tailCircle.set({ left: nextPosition.x, top: nextPosition.y });
    }

    // Update circles and redraw the canvas
    this.#headCircle.setCoords();
    this.#tailCircle.setCoords();
    this.#adjustArrowHead(isHead ? this.#headCircle : this.#tailCircle);
    canvas.renderAll();
  };

  get clonableProps() {
    const {
      left,
      top,
      width,
      angle,
      scaleX,
      scaleY,
      styles,
      fill,
      strokeWidth,
      strokeDashArray,
    } = this.ref;

    return {
      left,
      top,
      width,
      angle,
      scaleX,
      scaleY,
      styles,
      fill,
      strokeWidth,
      strokeDashArray,
    };
  }

  /**
   * Clone this line by returning a new instance.
   * Some of this is less straightforward than seems necessary, because we initially create
   * the line based on tail/head position but it remains a fabric object with a bounding box
   * at the angle of the line. If we pass the angle alongisde the current tail/head then the
   * line would be angled within the bounding box.
   */
  clone() {
    const { canvas, clonableProps } = this;

    const tailPoint = {
      x: this.#tailCircle.left,
      y: this.#tailCircle.top,
    };
    const headPoint = {
      x: this.#headCircle.left,
      y: this.#headCircle.top,
    };

    const pointDistance = this.getPointDistance(tailPoint, headPoint);
    const midPoint = this.getMidPoint(tailPoint, headPoint);

    const newLine = new BlueprintLine(canvas, {
      fabricOptions: clonableProps,
      coords: {
        tail: { x: tailPoint.x, y: tailPoint.y },
        /**
         * Just use the distance since it keeps it's angle of rotation, so
         * it maintains it's fabric object orientation
         */
        head: { x: tailPoint.x + pointDistance, y: tailPoint.y },
      },
      controls: {
        fill: this.#tailCircle.fill,
        stroke: this.#tailCircle.stroke,
      },
      arrow: !!this.#arrowHead,
    });

    newLine.setCenterPoint({
      x: midPoint.x + 20,
      y: midPoint.y + 20,
    });

    return newLine;
  }

  /**
   * Center the line (by moving it) in the current viewport
   */
  centerInViewport() {
    const { canvas } = this;
    const zoom = canvas.getZoom();

    this.setCenterPoint({
      x:
        fabric.util.invertTransform(canvas.viewportTransform)[4] +
        canvas.width / zoom / 2,
      y:
        fabric.util.invertTransform(canvas.viewportTransform)[5] +
        canvas.height / zoom / 2,
    });
  }

  /**
   * Return the line's center
   * This is a method because getters/setters aren't inherited in JS
   * @param {{ x: number, y: number }} point
   */
  setCenterPoint(point) {
    this.ref.setPositionByOrigin(point, 'center', 'center');
    this.ref.setCoords();
    this.#handleLineMovement();
    this.render();
  }
}
